import React, { useEffect, useMemo, useState, useRef } from 'react';
import rtoService from '../../services/rtoService';
import { Card, CardHeader, CardBody } from '../../components/card/card.jsx';

export default function Orchestrations() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extra insights
  const [targetsInfo, setTargetsInfo] = useState({ domains: [], dcs: [], hosts: [], cidrs: [] });
  const [aiSummary, setAiSummary] = useState('');

  // AI summarize controls
  const [aiJobId, setAiJobId] = useState(null);
  const [aiStatus, setAiStatus] = useState(null); // pending|running|completed|failed
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiTimerRef = useRef(null);

  // Filters & search for runs
  const [listQuery, setListQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all|pending|running|completed|failed
  const [sortBy, setSortBy] = useState('newest'); // newest|oldest

  // Filters for tools within a summary
  const [toolQuery, setToolQuery] = useState('');
  const [toolStatusFilter, setToolStatusFilter] = useState('all'); // all|pending|running|completed|failed

  // Auto-refresh controls
  const [autoRefresh, setAutoRefresh] = useState(true);
  const listTimerRef = useRef(null);
  const summaryTimerRef = useRef(null);

  // Anonymizer insights
  const [anonymizersUsed, setAnonymizersUsed] = useState([]);

  // Tor status states
  const [torStatus, setTorStatus] = useState(null);
  const [torExit, setTorExit] = useState(null);
  const [torLoading, setTorLoading] = useState(false);
  const [torError, setTorError] = useState(null);

  const refreshList = async () => {
    try {
      const orchestrations = await rtoService.listOrchestrations();
      const me = (await import('../../services/authService')).default.getCurrentUser();
      const role = me?.role;
      const myId = me?.id != null ? String(me.id) : null;
      const myOrg = me?.organisation ?? me?.organization ?? me?.org ?? me?.orgId ?? me?.org_id ?? null;
      const scoped = (Array.isArray(orchestrations) ? orchestrations : []).filter((o) => {
        if (role === 'admin') return true;
        const owner = o.user_id ?? o.owner_id ?? o.created_by ?? o.user ?? o.owner;
        const org = o.organisation ?? o.organization ?? o.org ?? o.orgId ?? o.org_id;
        if (role === 'superuser') {
          if (org != null && myOrg != null) return String(org) === String(myOrg);
          if (owner != null && myId != null) return String(owner) === String(myId);
          return false;
        }
        if (owner != null && myId != null) return String(owner) === String(myId);
        return false;
      });
      setList(scoped);
      if (scoped.length && !selected) {
        setSelected(scoped[0]);
      } else if (!scoped.length) {
        setSelected(null);
      }
    } catch (e) {
      setError(e.message || 'Failed to load orchestrations');
    }
  };

  const buildSyntheticSummary = async (orchId) => {
    const orch = await rtoService.getOrchestration(orchId).catch(() => null);
    const tasks = await rtoService.listTasks(orchId).catch(() => []);
    const notes = [];
    const prof = (orch?.profile || '').toLowerCase();
    if (prof.includes('plan') || prof.includes('dry')) notes.push('dry-run; plan only');
    return {
      id: orchId,
      status: orch?.status || 'unknown',
      created_at: orch?.created_at || null,
      tools: tasks || [],
      totals: {},
      notes,
      ai: { findings_total: 0 },
    };
  };

  const refreshSummary = async (orchId) => {
    if (!orchId) return;
    try {
      const s = await rtoService.getOrchestrationSummary(orchId);
      setSummary(s);
      // If backend provided anonymizer breakdown, prefer it
      try {
        const anon = new Set();
        const a = s?.anonymizer || {};
        (a.applied || []).forEach((n) => n && anon.add(String(n)));
        (a.requested || []).forEach((n) => n && anon.add(`${String(n)} (requested)`));
        (a.unavailable || []).forEach((n) => n && anon.add(`${String(n)} (unavailable)`));
        if (anon.size > 0) setAnonymizersUsed(Array.from(anon));
      } catch (_) {
        // noop
      }
      // Also load additional insights (targets + AI summary text)
      loadExtraInsights(orchId);
    } catch (e) {
      // Fallback: synthesize summary from available endpoints
      try {
        const synth = await buildSyntheticSummary(orchId);
        setSummary(synth);
        loadExtraInsights(orchId);
      } catch (err2) {
        setError(e.message || 'Failed to load summary');
      }
    }
  };

  async function loadExtraInsights(orchId) {
    try {
      const results = await rtoService.getOrchestrationResults(orchId);
      // Extract targets from policy.decision facts (requested_params / sanitized_params)
      const domains = new Set();
      const dcs = new Set();
      const hosts = new Set();
      const cidrs = new Set();
      const tryCollect = (obj) => {
        if (!obj || typeof obj !== 'object') return;
        const pushVal = (val, set) => {
          if (Array.isArray(val)) val.forEach((v) => v && set.add(String(v)));
          else if (val) set.add(String(val));
        };
        // Common target-ish keys
        pushVal(obj.domain, domains);
        pushVal(obj.dc, dcs);
        pushVal(obj.target, hosts);
        pushVal(obj.host, hosts);
        pushVal(obj.hostname, hosts);
        pushVal(obj.hosts, hosts);
        pushVal(obj.domains, domains);
        pushVal(obj.cidrs, cidrs);
        pushVal(obj.networks, cidrs);
        pushVal(obj.ips, hosts);
        // Nested targets object
        if (obj.targets && typeof obj.targets === 'object') tryCollect(obj.targets);
      };
      (results.facts || []).forEach((f) => {
        if (!f || f.kind !== 'policy.decision') return;
        const d = f.data || {};
        tryCollect(d.requested_params);
        tryCollect(d.sanitized_params);
      });
      setTargetsInfo({
        domains: Array.from(domains),
        dcs: Array.from(dcs),
        hosts: Array.from(hosts),
        cidrs: Array.from(cidrs),
      });

      // Collect anonymizer usage facts and merge with any summary-provided badges
      const anonSetFromResults = new Set();
      (results.facts || []).forEach((f) => {
        if (!f) return;
        const k = f.kind || '';
        const d = f.data || {};
        if (k === 'anonymizer.applied') {
          const name = (d.name || d.anonymizer || '').toString().trim();
          if (name) anonSetFromResults.add(name);
        }
        if (k === 'anonymizer.requested') {
          const name = (d.name || d.anonymizer || '').toString().trim();
          if (name) anonSetFromResults.add(name + ' (requested)');
        }
        if (k === 'anonymizer.unavailable') {
          const name = (d.name || d.anonymizer || '').toString().trim();
          if (name) anonSetFromResults.add(name + ' (unavailable)');
        }
      });
      if (anonSetFromResults.size > 0) {
        setAnonymizersUsed((prev) => Array.from(new Set([...(prev || []), ...Array.from(anonSetFromResults)])));
      }

      // Try to find AI summary artifact and fetch text
      let aiText = '';
      const aiArt = (results.artifacts || []).find((a) => (a.type || '').toLowerCase() === 'ai_summary');
      if (aiArt && aiArt.id) {
        try {
          const content = await rtoService.getArtifactContent(aiArt.id);
          if (typeof content === 'string') {
            aiText = content;
          } else if (content && typeof content === 'object') {
            aiText = content.summary || content.text || content.content || JSON.stringify(content);
          }
        } catch (_) {
          // ignore
        }
      }
      setAiSummary(aiText);
    } catch (e) {
      // Non-fatal for UI; keep previous targets/aiSummary if any
    }
  }

  async function refreshTorStatus() {
    setTorError(null);
    try {
      const s = await rtoService.torStatus();
      setTorStatus(s);
    } catch (e) {
      setTorError(e.message || 'Failed to fetch Tor status');
      setTorStatus(null);
    }
  }

  async function testViaTor() {
    setTorLoading(true);
    setTorError(null);
    setTorExit(null);
    try {
      const res = await rtoService.torExitIP();
      setTorExit(res);
    } catch (e) {
      setTorError(e.message || 'Failed to fetch Tor exit IP');
    } finally {
      setTorLoading(false);
    }
  }

  // --- AI summarize helpers ---
  const stopAiPolling = () => {
    if (aiTimerRef.current) {
      clearInterval(aiTimerRef.current);
      aiTimerRef.current = null;
    }
  };

  const startAiPolling = (jobId, orchId) => {
    stopAiPolling();
    aiTimerRef.current = setInterval(async () => {
      try {
        const st = await rtoService.aiJobStatus(jobId);
        setAiStatus(st.status);
        if (st.status === 'completed' || st.status === 'failed') {
          stopAiPolling();
          setAiLoading(false);
          // Fetch results if completed
          if (st.status === 'completed') {
            try {
              const res = await rtoService.aiJobResults(jobId);
              const txt = res?.summary || '';
              if (txt) setAiSummary(txt);
            } catch (err) {
              setAiError(err?.message || 'Failed to fetch AI results');
            }
          }
          // Refresh orchestration summary & extras
          if (orchId) {
            refreshSummary(orchId);
            loadExtraInsights(orchId);
          }
        }
      } catch (e) {
        // Stop polling on error, but surface it
        stopAiPolling();
        setAiLoading(false);
        setAiError(e.message || 'AI job polling failed');
      }
    }, 1500);
  };

  const runAISummary = async () => {
    if (!selected?.id) return;
    setAiError(null);
    setAiLoading(true);
    setAiStatus('pending');
    setAiJobId(null);
    try {
      const resp = await rtoService.aiSummarizeStart({ orchestration_id: selected.id });
      const jid = resp?.id;
      setAiJobId(jid);
      setAiStatus(resp?.status || 'pending');
      if (jid) startAiPolling(jid, selected.id);
    } catch (e) {
      setAiError(e.message || 'Failed to start AI summarize');
      setAiLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    refreshList().finally(() => setLoading(false));
    // initial tor status
    refreshTorStatus();
    const id = setInterval(() => refreshTorStatus(), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { if (selected?.id) refreshSummary(selected.id); }, [selected?.id]);

  // Periodic auto-refresh: list (every 20s) and summary (every 5s until completed)
  useEffect(() => {
    if (listTimerRef.current) clearInterval(listTimerRef.current);
    if (summaryTimerRef.current) clearInterval(summaryTimerRef.current);

    if (!autoRefresh) return; // user paused

    listTimerRef.current = setInterval(() => {
      refreshList();
    }, 20000);

    summaryTimerRef.current = setInterval(() => {
      if (!selected?.id) return;
      const isDone = (summary?.status || '').toLowerCase() === 'completed' || (summary?.status || '').toLowerCase() === 'failed';
      if (!isDone) refreshSummary(selected.id);
    }, 5000);

    return () => {
      if (listTimerRef.current) clearInterval(listTimerRef.current);
      if (summaryTimerRef.current) clearInterval(summaryTimerRef.current);
      stopAiPolling();
    };
  }, [autoRefresh, selected?.id, summary?.status]);

  const filteredSortedList = useMemo(() => {
    let arr = (list || []).slice();
    if (statusFilter !== 'all') {
      arr = arr.filter(o => (o.status || '').toLowerCase() === statusFilter);
    }
    if (listQuery.trim()) {
      const q = listQuery.trim().toLowerCase();
      arr = arr.filter(o => `${o.id}`.includes(q) || (o.profile || '').toLowerCase().includes(q));
    }
    arr.sort((a,b) => sortBy === 'newest' ? ((b.id||0)-(a.id||0)) : ((a.id||0)-(b.id||0)));
    return arr;
  }, [list, listQuery, statusFilter, sortBy]);

  const filteredTools = useMemo(() => {
    const tools = (summary?.tools || []).slice();
    let arr = tools;
    if (toolStatusFilter !== 'all') {
      arr = arr.filter(t => (t.status || '').toLowerCase() === toolStatusFilter);
    }
    if (toolQuery.trim()) {
      const q = toolQuery.trim().toLowerCase();
      arr = arr.filter(t => `${t.id}`.includes(q) || (t.tool || '').toLowerCase().includes(q));
    }
    return arr;
  }, [summary?.tools, toolQuery, toolStatusFilter]);

  // Simple bar visualization helper
  const Bar = ({ label, value = 0, max = 1, color = 'primary' }) => {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
      <div className="mb-2">
        <div className="d-flex justify-content-between small"><span>{label}</span><span>{value}</span></div>
        <div className="progress" style={{ height: 8 }}>
          <div className={`progress-bar bg-${color}`} role="progressbar" style={{ width: `${pct}%` }} aria-valuenow={value} aria-valuemin="0" aria-valuemax={max}></div>
        </div>
      </div>
    );
  };

  const totals = summary?.totals || {};
  const enum4 = totals.enum4linux || { domains: 0, shares: 0, users: 0 };
  const ldap = totals.ldap || { entries: 0 };
  const imp = totals.impacket_getadusers || { users_total: 0, asrep_roastable: 0 };
  const bh = totals.bloodhound || { file_count: 0, node_counts: {} };
  const nodeCounts = bh.node_counts || {};
  const maxNodeCount = Object.values(nodeCounts).reduce((m, v) => Math.max(m, Number(v) || 0), 0);

  const selectedId = selected?.id;

  const statusBadgeClass = (st) => {
    const s = (st || '').toLowerCase();
    if (s === 'completed') return 'badge bg-success';
    if (s === 'running') return 'badge bg-info';
    if (s === 'failed') return 'badge bg-danger';
    if (s === 'pending') return 'badge bg-secondary';
    return 'badge bg-light text-dark';
  };

  // Duration helpers
  const formatDuration = (ms = 0) => {
    if (!ms || ms < 0) return '0s';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const parts = [];
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    parts.push(`${sec}s`);
    return parts.join(' ');
  };

  const computeTimesFromTasks = (tasks = []) => {
    let start = null;
    let end = null;
    let anyStarted = false;
    let anyRunning = false;
    (tasks || []).forEach((t) => {
      const st = t?.started_at ? new Date(t.started_at).getTime() : null;
      const ft = t?.finished_at ? new Date(t.finished_at).getTime() : null;
      if (st) {
        anyStarted = true;
        start = start == null ? st : Math.min(start, st);
      }
      if (ft) {
        end = end == null ? ft : Math.max(end, ft);
      }
      if (t && String(t.status || '').toLowerCase() === 'running') anyRunning = true;
    });
    const now = Date.now();
    const effectiveEnd = end != null ? end : (anyRunning ? now : null);
    const ms = (start != null && effectiveEnd != null) ? Math.max(0, effectiveEnd - start) : 0;
    return { start, end: effectiveEnd, ms, anyStarted, anyRunning };
  };

  const DurationBadge = ({ tasks = [] }) => {
    const t = computeTimesFromTasks(tasks);
    if (!t.ms) return null;
    const title = `${t.start ? new Date(t.start).toLocaleString() : ''} - ${t.end ? new Date(t.end).toLocaleString() : 'running'}`.trim();
    return (
      <span className="badge bg-light text-dark" title={title}>
        <i className="bi bi-stopwatch me-1"></i>
        {formatDuration(t.ms)}
      </span>
    );
  };

  const torBadge = () => {
    if (!torStatus) return <span className="badge bg-light text-dark">Tor: unknown</span>;
    const avail = !!torStatus.available;
    const torsocks = !!torStatus.torsocks_present;
    const ver = torStatus.tor_version || '';
    const conn = torStatus.control_connected === true;
    const cls = avail ? (conn ? 'bg-success' : 'bg-warning text-dark') : 'bg-danger';
    const title = avail ? (conn ? 'Tor connected' : 'Tor available, not connected') : 'Tor not available';
    return <span className={`badge ${cls}`} title={title}>{ver?` ${ver}:`:''}{torsocks?' ts':''}</span>;
  };

  // Add compact icon indicators for Tor status
  const torStatusIcons = () => {
    const s = torStatus || {};
    const Icon = ({ ok, on, off, title }) => (
      <i className={`bi ${ok ? on : off} ${ok ? 'text-success' : 'text-muted'}`} title={title}></i>
    );
    return (
      <span className="d-inline-flex align-items-center gap-2">
        <Icon ok={!!s.torsocks_present} on="bi-shield-lock-fill" off="bi-shield" title="torsocks installed" />
        <Icon ok={!!s.control_connected} on="bi-plug-fill" off="bi-plug" title="Tor control port reachable" />
        <Icon ok={!!s.available} on="bi-wifi" off="bi-wifi-off" title={s.note || 'Tor availability'} />
      </span>
    );
  };

  const renderAnonBadges = () => (
    <>
      {anonymizersUsed.length > 0 && anonymizersUsed.map((a, idx) => (
        <span key={`anon-${idx}`} className="badge bg-dark">Anonymizer: {a}</span>
      ))}
    </>
  );

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Orchestrations</h4>
        <div className="d-flex align-items-center gap-2">
          {/* Moved Anonymizer inline panel to top */}
          <div className="d-flex align-items-center gap-2 me-3">
            <span className="fw-bold small">Anonymizer</span>
            {torBadge()}
            {torStatusIcons()}
            {renderAnonBadges()}
            <button className="btn btn-sm btn-warning" onClick={testViaTor} disabled={torLoading} title="Check Tor exit IP">
              {torLoading ? (
                <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Testing…</>
              ) : (
                <><i className="bi bi-shield-lock me-1"></i>Test via Tor</>
              )}
            </button>
            {torExit?.ip && (
              <span className="badge bg-dark" title={`Via: ${torExit.via || 'tor'}`}>Exit IP: {torExit.ip}</span>
            )}
            <button className="btn btn-sm btn-outline-secondary" onClick={refreshTorStatus} title="Refresh Tor status">
              <i className="bi bi-arrow-clockwise"></i>
            </button>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" id="autoRefreshSwitch" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            <label className="form-check-label" htmlFor="autoRefreshSwitch">Auto-refresh</label>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => refreshList()} disabled={loading}>Refresh List</button>
          {selectedId && (
            <>
              <button className="btn btn-sm btn-outline-primary" onClick={() => refreshSummary(selectedId)} disabled={loading}>Refresh Summary</button>
              <button className="btn btn-sm btn-success" onClick={runAISummary} disabled={aiLoading} title="Run AI summarization for this orchestration">
                {aiLoading ? (
                  <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Running AI…</>
                ) : (
                  <><i className="bi bi-stars me-1"></i>Run AI Summary</>
                )}
              </button>
              {aiJobId && (
                <span className={`badge ${aiStatus==='completed'?'bg-success':aiStatus==='failed'?'bg-danger':'bg-info'}`} title={`AI Job #${aiJobId}`}>AI: {aiStatus || '...'}</span>
              )}
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {aiError && (
        <div className="alert alert-warning py-2 my-2 small" role="alert">{aiError}</div>
      )}

      <div className="row g-3">
        <div className="col-md-4">
          <Card>
            <CardHeader>
              <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <strong>Runs</strong>
                  <span className="badge bg-light text-dark">{filteredSortedList.length}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <input type="text" className="form-control form-control-sm" style={{ minWidth: 140 }} placeholder="Search id/profile" value={listQuery} onChange={(e)=>setListQuery(e.target.value)} />
                  <select className="form-select form-select-sm" value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                  <select className="form-select form-select-sm" value={sortBy} onChange={(e)=>setSortBy(e.target.value)}>
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="list-group list-group-flush" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {filteredSortedList.map((o) => (
                  <button
                    key={o.id}
                    className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedId===o.id?'active':''}`}
                    onClick={() => setSelected(o)}
                  >
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <strong>#{o.id}</strong> <span className="text-muted">·</span> <span>{o.profile}</span>
                        <span className={statusBadgeClass(o.status)}>{o.status}</span>
                        {(() => {
                          const prof = (o.profile || '').toLowerCase();
                          const isPlan = prof.includes('plan') || prof.includes('dry');
                          return isPlan ? <span className="badge bg-warning text-dark" title="Plan (dry-run) mode">Plan</span> : null;
                        })()}
                        {Array.isArray(o.tasks) && o.tasks.length > 0 && (
                          <DurationBadge tasks={o.tasks} />
                        )}
                      </div>
                      <div className="small text-muted">{o.created_at ? new Date(o.created_at).toLocaleString() : ''}</div>
                    </div>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                ))}
                {filteredSortedList.length === 0 && (
                  <div className="list-group-item text-muted">No orchestrations yet.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-8">
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <strong>Summary</strong>
              {selectedId && (
                <span className="text-muted small">Orch #{selectedId}</span>
              )}
            </CardHeader>
            <CardBody>
              {/* Anonymizer panel moved to top header */}
              {!selectedId && (
                <div className="text-muted">Select an orchestration to view details.</div>
              )}
              {selectedId && !summary && (
                <div className="text-muted">Loading summary...</div>
              )}
              {summary && (
                <div className="d-flex flex-column gap-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className={statusBadgeClass(summary.status)}>Status: {summary.status}</span>
                    <span className="badge bg-info">AI Findings: {summary.ai?.findings_total ?? 0}</span>
                    <span className="badge bg-light text-dark">Tools: {(summary.tools || []).length}</span>
                    {(() => {
                      const n = (summary.notes || []).join(' ').toLowerCase();
                      const isPlan = n.includes('dry-run') || n.includes('plan only');
                      return isPlan ? <span className="badge bg-warning text-dark" title="Plan (dry-run) mode">Plan</span> : null;
                    })()}
                    {renderAnonBadges()}
                    <DurationBadge tasks={summary.tools || []} />
                    {summary.created_at && (
                      <span className="badge bg-light text-dark">{new Date(summary.created_at).toLocaleString()}</span>
                    )}
                    {torBadge()}
                    {torStatusIcons()}
                  </div>

                  {/* Targets */}
                  <div>
                    <div className="fw-bold mb-2">Targets</div>
                    {(!targetsInfo.domains.length && !targetsInfo.dcs.length && !targetsInfo.hosts.length && !targetsInfo.cidrs.length) && (
                      <div className="text-muted small">No explicit targets captured.</div>
                    )}
                    <div className="d-flex flex-wrap gap-2 small">
                      {targetsInfo.domains.slice(0,3).map((d,idx) => (<span key={`dom-${idx}`} className="badge bg-secondary">domain: {d}</span>))}
                      {targetsInfo.dcs.slice(0,2).map((d,idx) => (<span key={`dc-${idx}`} className="badge bg-secondary">dc: {d}</span>))}
                      {targetsInfo.hosts.slice(0,5).map((h,idx) => (<span key={`host-${idx}`} className="badge bg-secondary">host: {h}</span>))}
                      {targetsInfo.cidrs.slice(0,3).map((c,idx) => (<span key={`cidr-${idx}`} className="badge bg-secondary">cidr: {c}</span>))}
                      {targetsInfo.hosts.length > 5 && (
                        <span className="badge bg-light text-dark">+{targetsInfo.hosts.length - 5} more hosts</span>
                      )}
                    </div>
                  </div>

                  {/* Textual Summary */}
                  <div>
                    <div className="fw-bold mb-2">Summary</div>
                    {aiSummary ? (
                      <pre className="small mb-0" style={{ maxHeight: '30vh', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{aiSummary}</pre>
                    ) : (
                      <div className="text-muted small">No AI summary available. See Notes and Totals below.</div>
                    )}
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="border rounded p-2 h-100">
                        <div className="fw-bold mb-2">Tools</div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <input type="text" className="form-control form-control-sm" placeholder="Filter tools" value={toolQuery} onChange={(e)=>setToolQuery(e.target.value)} />
                          <select className="form-select form-select-sm" value={toolStatusFilter} onChange={(e)=>setToolStatusFilter(e.target.value)}>
                            <option value="all">All</option>
                            <option value="pending">Pending</option>
                            <option value="running">Running</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                          </select>
                        </div>
                        <ul className="small mb-0" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                          {filteredTools.map((t, i) => (
                            <li key={i} className="d-flex align-items-center gap-2">
                              <span>#{t.id} · {t.tool}</span>
                              <span className={statusBadgeClass(t.status)}>{t.status}</span>
                            </li>
                          ))}
                          {filteredTools.length === 0 && (
                            <li className="text-muted">No tools match.</li>
                          )}
                        </ul>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-2 h-100">
                        <div className="fw-bold mb-2">Totals</div>
                        <div className="small text-muted mb-2">Enum4linux</div>
                        <Bar label="Domains" value={enum4.domains||0} max={Math.max(enum4.domains||0, enum4.shares||0, enum4.users||0, 1)} color="primary" />
                        <Bar label="Shares" value={enum4.shares||0} max={Math.max(enum4.domains||0, enum4.shares||0, enum4.users||0, 1)} color="success" />
                        <Bar label="Users" value={enum4.users||0} max={Math.max(enum4.domains||0, enum4.shares||0, enum4.users||0, 1)} color="info" />
                        <div className="small text-muted mt-3 mb-2">LDAP</div>
                        <Bar label="Entries" value={ldap.entries||0} max={Math.max(ldap.entries||0, 1)} color="secondary" />
                        <div className="small text-muted mt-3 mb-2">Impacket getADUsers</div>
                        <Bar label="Users (total)" value={imp.users_total||0} max={Math.max(imp.users_total||0, 1)} color="warning" />
                        <Bar label="AS-REP roastable" value={imp.asrep_roastable||0} max={Math.max(imp.asrep_roastable||0, 1)} color="danger" />
                        <div className="small text-muted mt-3 mb-2">BloodHound</div>
                        <Bar label="Files" value={bh.file_count||0} max={Math.max(bh.file_count||0, 1)} color="dark" />
                        {Object.keys(nodeCounts).length > 0 && (
                          <div className="mt-2">
                            {Object.entries(nodeCounts).map(([k,v]) => (
                              <Bar key={k} label={k} value={Number(v)||0} max={Math.max(maxNodeCount, 1)} color="primary" />
                            ))}
                          </div>
                        )}
                        <div className="mt-2 small text-muted d-flex align-items-center gap-2">
                          <i className="bi bi-stopwatch"></i>
                          <span>Execution time: {(() => {
                            const t = computeTimesFromTasks(summary?.tools || []);
                            return formatDuration(t.ms);
                          })()}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  {(summary.notes || []).length > 0 && (
                    <div>
                      <div className="fw-bold mb-2">Notes</div>
                      <ul className="small mb-0" style={{ maxHeight: '24vh', overflowY: 'auto' }}>
                        {summary.notes.map((n, idx) => <li key={idx}>{String(n)}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}